import React from 'react';
import Icon from '../../../components/AppIcon';

const ModuleResourceManagement = ({ resources }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'allocated':
        return 'var(--color-success)';
      case 'shortage':
        return 'var(--color-error)';
      case 'excess':
        return 'var(--color-warning)';
      case 'available':
        return 'var(--color-primary)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getVarianceColor = (actual, planned) => {
    const variance = ((actual - planned) / planned) * 100;
    if (variance > 10) return 'var(--color-error)';
    if (variance < -10) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const calculateVariance = (actual, planned) => {
    if (!planned || planned === 0) return 0;
    return ((actual - planned) / planned) * 100;
  };

  const groupedResources = {
    manpower: resources?.filter(r => r?.resourceType === 'manpower') || [],
    equipment: resources?.filter(r => r?.resourceType === 'equipment') || [],
    materials: resources?.filter(r => r?.resourceType === 'materials') || []
  };

  const renderResourceSection = (title, items, icon) => (
    <div className="mb-4 last:mb-0">
      <div className="flex items-center gap-2 mb-3">
        <Icon name={icon} size={16} color="var(--color-primary)" />
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        <span className="text-xs text-muted-foreground">({items?.length})</span>
      </div>
      
      {items?.length === 0 ? (
        <p className="text-xs text-muted-foreground ml-6">No {title?.toLowerCase()} resources allocated</p>
      ) : (
        <div className="space-y-2">
          {items?.map((resource) => {
            const variance = calculateVariance(resource?.actualQuantity, resource?.plannedQuantity);
            return (
              <div key={resource?.id} className="bg-muted/30 rounded-lg p-3 border border-border">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{resource?.resourceName}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <div className="text-xs">
                        <span className="text-muted-foreground">Planned: </span>
                        <span className="font-semibold text-foreground">{resource?.plannedQuantity} {resource?.unit}</span>
                      </div>
                      <div className="text-xs">
                        <span className="text-muted-foreground">Actual: </span>
                        <span className="font-semibold" style={{ color: getVarianceColor(resource?.actualQuantity, resource?.plannedQuantity) }}>
                          {resource?.actualQuantity} {resource?.unit}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div 
                      className="text-xs px-2 py-1 rounded-full mb-1"
                      style={{
                        backgroundColor: `${getStatusColor(resource?.status)}20`,
                        color: getStatusColor(resource?.status)
                      }}
                    >
                      {resource?.status?.charAt(0)?.toUpperCase() + resource?.status?.slice(1)}
                    </div>
                    <p className="text-xs font-semibold text-foreground">{resource?.utilizationPercentage}%</p>
                  </div>
                </div>
                
                {/* Variance Indicator */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        width: `${resource?.utilizationPercentage}%`,
                        backgroundColor: getStatusColor(resource?.status)
                      }}
                    />
                  </div>
                  <span 
                    className="text-xs font-medium whitespace-nowrap"
                    style={{ color: getVarianceColor(resource?.actualQuantity, resource?.plannedQuantity) }}
                  >
                    {variance > 0 ? '+' : ''}{variance?.toFixed(1)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-card rounded-xl border border-border p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Package" size={16} color="var(--color-primary)" />
        </div>
        <h3 className="text-base font-semibold text-foreground">Resource Management</h3>
        <span className="text-xs text-muted-foreground ml-auto">Planned vs Used</span>
      </div>

      {!resources || resources?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Package" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No resource data available</p>
        </div>
      ) : (
        <div className="max-h-[400px] overflow-y-auto">
          {renderResourceSection('Manpower', groupedResources?.manpower, 'Users')}
          {renderResourceSection('Equipment', groupedResources?.equipment, 'Wrench')}
          {renderResourceSection('Materials', groupedResources?.materials, 'Box')}
        </div>
      )}
    </div>
  );
};

export default ModuleResourceManagement;